import * as React from 'react';
import {
  HeadingText,
  Modal,
  StackLayout,
  ParagraphText,
  Button,
} from '@leagueplatform/genesis-core';
import { Badge } from '@leagueplatform/web-common-components';
import {
  FieldType,
  InputConfigValue,
  EffectiveTime,
} from '@leagueplatform/dashboard-api';
import { InformationBanner } from '#components/information-banner/information-banner.tsx';
import { FormProvider } from '@leagueplatform/web-common';
import { FieldValues, useForm } from 'react-hook-form';
import { useIntl } from '@leagueplatform/locales';
import {
  CheckboxList,
  CheckboxFieldConfig,
} from '#components/inputs/checkbox-list.tsx';
import { usePatchHealthMetrics } from '#hooks/use-patch-health-metrics.hook.ts';
import { usePostHealthMetrics } from '#hooks/use-post-health-metrics.hook.ts';
import { formatMetricData } from '#utils/format-metric-data.util.ts';
import { parseISO } from 'date-fns';
import { RadioInput } from '#components/inputs/radio-input.tsx';
import { DateTimeInput } from '#components/inputs/date-time-input.tsx';
import { parseAbsoluteToLocal } from '@internationalized/date';
import { IntegerInput } from '#components/inputs/integer-input.tsx';
import { ErrorSaving } from '#components/manage-units-modal/manage-units-modal.tsx';
import { track } from '@leagueplatform/analytics';
import { InputValue } from '#utils/parse-value-for-input.util.ts';

type MetricFormValueEntry = {
  observationId?: string;
  value: InputValue;
};

type MetricFormValues = {
  timeStamp?: string;
  effectiveDateTime?: EffectiveTime;
  metrics: MetricFormValueEntry[];
};

type AddEditDataModalContentProps = {
  inputConfig: InputConfigValue;
  metricConfigId: string;
  onSuccessSubmit: () => void;
  onAddData?: (date: Date) => void;
  values?: MetricFormValues;
};

export function AddEditDataModalContent({
  inputConfig,
  metricConfigId,
  onSuccessSubmit,
  onAddData,
  values,
}: AddEditDataModalContentProps) {
  const {
    badge,
    heading,
    description,
    healthMetricInputs,
    showEffectiveDateTimePicker,
    informationBanner,
  } = inputConfig;

  const { formatMessage } = useIntl();
  const formMethods = useForm({ mode: 'onChange' });

  // Iterate through metric inputs to find all boolean fields for checkbox
  // list which will be rendered within the same group.
  const checkboxFields: CheckboxFieldConfig[] = healthMetricInputs.flatMap(
    (healthMetricInput, metricIndex) =>
      healthMetricInput.valueFields
        .filter((valueField) => valueField.fieldType === FieldType.BOOLEAN)
        .map((valueField) => ({
          metricIndex,
          valueField,
          defaultValue:
            values?.metrics?.[metricIndex]?.value?.[valueField.fieldKeyName],
        })),
  );

  const postHealthMetricsMutation = usePostHealthMetrics();
  const patchHealthMetricsMutation = usePatchHealthMetrics();

  const isLoading =
    postHealthMetricsMutation.isLoading || patchHealthMetricsMutation.isLoading;
  const isError =
    postHealthMetricsMutation.isError || patchHealthMetricsMutation.isError;

  return (
    <>
      <StackLayout
        horizontalAlignment="center"
        css={{ paddingBottom: '$two' }}
        spacing="$one"
      >
        <Badge
          css={{
            backgroundColor: '$decorativeBrandPrimaryBrightest',
            borderRadius: '$small',
          }}
        >
          {badge}
        </Badge>
        <Modal.Title>
          <HeadingText level="2" size="xl" css={{ textAlign: 'center' }}>
            {heading}
          </HeadingText>
        </Modal.Title>
        {description && (
          <ParagraphText css={{ textAlign: 'center' }}>
            {description}
          </ParagraphText>
        )}
        {checkboxFields.length > 0 ? (
          <ParagraphText css={{ textAlign: 'center' }}>
            {formatMessage({ id: 'SELECT_ALL_THAT_APPLY' })}
          </ParagraphText>
        ) : null}
      </StackLayout>
      <StackLayout horizontalAlignment="stretch" spacing="$two">
        <FormProvider
          formMethods={formMethods}
          onSubmit={async (formData: FieldValues) => {
            const formattedMetricData = formatMetricData(
              formData,
              healthMetricInputs,
              metricConfigId,
            );

            if (values) {
              // UPDATE
              const formattedMetricDataWithObservationId =
                formattedMetricData.map((data, index) => ({
                  ...data,
                  observationId: values.metrics[index].observationId!,
                }));

              patchHealthMetricsMutation.mutate(
                formattedMetricDataWithObservationId,
                {
                  onSuccess() {
                    onSuccessSubmit();

                    track(
                      'dashboard.button_clicked.metric_data_entry_screen_metric_type.submit',
                      {
                        metric_type: metricConfigId,
                        metric_id: '',
                        metric_status: '',
                      },
                    );
                  },
                },
              );
            } else {
              //  CREATE
              postHealthMetricsMutation.mutate(formattedMetricData, {
                onSuccess() {
                  onSuccessSubmit();

                  if (onAddData) {
                    // This datetime is used by the metric details page to automatically switch the chart to the
                    // metric that was just created.  It's not used by the newer Masonry rewrite.
                    const effectiveDateTime = new Date(
                      parseISO(
                        formattedMetricData[0]?.effectiveDateTime ??
                          new Date().toISOString(),
                      ),
                    );
                    onAddData(effectiveDateTime);
                  }

                  track(
                    'dashboard.button_clicked.metric_data_entry_screen_metric_type.submit',
                    {
                      metric_type: metricConfigId,
                      metric_id: '',
                      metric_status: '',
                    },
                  );
                },
              });
            }
          }}
        >
          <StackLayout spacing="$one" horizontalAlignment="stretch">
            {showEffectiveDateTimePicker && (
              <DateTimeInput
                id="effectiveDateTime"
                validationMessage="validationMessage"
                label={formatMessage({ id: 'DATE_AND_TIME' })}
                defaultValue={values?.timeStamp}
                // Can only select the datetime when adding new data, not when editing existing data.
                isReadOnly={!!values}
              />
            )}
            {healthMetricInputs.map((healthMetricInput, index) =>
              healthMetricInput.valueFields
                .filter(
                  (valueField) => valueField.fieldType !== FieldType.BOOLEAN,
                )
                .map((valueField) => {
                  const fieldKey = `${index}-${valueField.fieldKeyName}`;

                  if (valueField.options) {
                    return (
                      <RadioInput
                        key={fieldKey}
                        id={fieldKey}
                        fieldLabel={valueField.fieldLabel}
                        options={valueField.options}
                        validationMessage={formatMessage({
                          id: 'NO_VALUE_SELECTED_ERROR_MESSAGE',
                        })}
                        hint={valueField.hint}
                        defaultValue={
                          values?.metrics[index].value[valueField.fieldKeyName]
                        }
                      />
                    );
                  }

                  switch (valueField.fieldType) {
                    case FieldType.DATETIME: {
                      return (
                        <DateTimeInput
                          key={fieldKey}
                          id={fieldKey}
                          label={valueField.fieldLabel}
                          validationMessage={formatMessage({
                            id: 'TOOLBOX_VALIDATION_REQUIRED_FIELD',
                          })}
                          defaultValue={
                            values?.effectiveDateTime?.[
                              valueField.fieldKeyName as keyof EffectiveTime
                            ]
                          }
                          required={!!values}
                          triggerValidation={() => formMethods.trigger()}
                          hint={valueField.hint}
                          validations={{
                            endDateMustBeAfterStart: (
                              fieldValue: string,
                              fieldValues: FieldValues,
                            ) => {
                              const startDate = parseAbsoluteToLocal(
                                fieldValues[`${index}-start`],
                              );
                              const endDate = parseAbsoluteToLocal(
                                fieldValues[`${index}-end`],
                              );

                              if (
                                fieldValue === fieldValues[`${index}-start`]
                              ) {
                                return undefined;
                              }

                              const check = startDate.compare(endDate);

                              // 60000 milliseconds = 1 minute, end date must be at least 1 minute after start date
                              if (check >= -60000) {
                                return formatMessage({
                                  id: 'START_DATE_MUST_BE_EARLIER_ERROR_MESSAGE',
                                });
                              }
                              return undefined;
                            },
                            notMoreThan24HoursDifference: (
                              fieldValue: string,
                              fieldValues: FieldValues,
                            ) => {
                              const startDate = parseAbsoluteToLocal(
                                fieldValues[`${index}-start`],
                              );
                              const endDate = parseAbsoluteToLocal(
                                fieldValues[`${index}-end`],
                              );
                              const check = startDate
                                .add({ days: 1 })
                                .compare(endDate);

                              if (
                                fieldValue === fieldValues[`${index}-start`]
                              ) {
                                return undefined;
                              }
                              if (check < 0) {
                                return formatMessage({
                                  id: 'DURATION_EXCEEDS_24_HOURS_ERROR_MESSAGE',
                                });
                              }
                              return undefined;
                            },
                          }}
                        />
                      );
                    }
                    case FieldType.QUANTITY: {
                      let defaultValue: number | undefined;
                      if (values) {
                        defaultValue =
                          healthMetricInput.valueFields.length === 1
                            ? values.metrics[index].value[
                                valueField.fieldKeyName
                              ]
                            : values.metrics[index].value[valueField.unit];
                      }

                      return (
                        <IntegerInput
                          key={fieldKey}
                          validationMessage={formatMessage({
                            id: 'TOOLBOX_VALIDATION_REQUIRED_FIELD',
                          })}
                          id={`input-${valueField.fieldKeyName}`}
                          unit={valueField.unit}
                          placeholder={valueField.placeholderCopy}
                          minValue={valueField.min}
                          maxValue={valueField.max}
                          name={`${index}-${valueField.fieldKeyName}`}
                          label={valueField.fieldLabel}
                          hint={valueField.hint}
                          defaultValue={defaultValue}
                        />
                      );
                    }
                    default: {
                      return null;
                    }
                  }
                }),
            )}
            {checkboxFields.length > 0 ? (
              <CheckboxList
                checkboxFields={checkboxFields}
                validationMessage={formatMessage({
                  id: 'NO_VALUE_SELECTED_ERROR_MESSAGE',
                })}
              />
            ) : null}
            {isError ? <ErrorSaving /> : null}
          </StackLayout>

          <Button
            loading={isLoading}
            icon="interfaceCheckmark"
            hideLabel
            type="submit"
            css={{
              position: 'absolute',
              bottom: '$minusFive',
              insetInlineEnd: '$none',
              '@mobile': {
                bottom: '$two',
                insetInlineEnd: '$two',
              },
            }}
          >
            {formatMessage({ id: 'SAVE' })}
          </Button>
        </FormProvider>

        {informationBanner && !!informationBanner.body && (
          <InformationBanner
            body={informationBanner.body}
            heading={informationBanner.heading}
            iconURL={informationBanner.icon}
          />
        )}
      </StackLayout>
    </>
  );
}
